import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { scrollableStepperCSS } from './ScrollableStepper.css';
import { stepperTooltipCSS } from './Cotizaciones.css';

const STEP_WIDTH = 76;
const SCROLL_DELAY = 220;

// Estados "completed-like" para el conector que sale del paso. Los nuevos
// `upcoming` / `cancelled` / `expired` son overlays visuales del paso Seguros
// — el hito ya fue completado, por eso se tratan igual que `done` para la línea.
const isCompletedLike = (state) =>
    state === 'done' ||
    state === 'warn' ||
    state === 'upcoming' ||
    state === 'cancelled' ||
    state === 'expired';

const CheckGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12l4 4 10-10" />
    </svg>
);

const XGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M6 18 18 6" />
    </svg>
);

// Versión "outline" más delgada para distinguir cancelado (naranja, no bloquea)
// del rechazo `rej` (rojo, bloqueante).
const XOutlineGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M6 18 18 6" />
    </svg>
);

const WarnGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
    </svg>
);

// Reloj — usado para `upcoming` (póliza próxima a iniciar).
const ClockGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
    </svg>
);

// Hourglass con arena abajo — usado para `expired` (vigencia vencida).
const HourglassEndGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 3h10" />
        <path d="M7 21h10" />
        <path d="M7 3v3a5 5 0 0 0 5 5 5 5 0 0 1 5 5v5" />
        <path d="M17 3v3a5 5 0 0 1-5 5 5 5 0 0 0-5 5v5" />
        <path d="M9 20h6" />
    </svg>
);

const renderGlyph = (state, number) => {
    if (state === 'done')      return <span className="ss-glyph"><CheckGlyph /></span>;
    if (state === 'warn')      return <span className="ss-glyph"><WarnGlyph /></span>;
    if (state === 'rej')       return <span className="ss-glyph"><XGlyph /></span>;
    if (state === 'upcoming')  return <span className="ss-glyph"><ClockGlyph /></span>;
    if (state === 'cancelled') return <span className="ss-glyph"><XOutlineGlyph /></span>;
    if (state === 'expired')   return <span className="ss-glyph"><HourglassEndGlyph /></span>;
    if (state === 'na')        return <span>–</span>;
    return <span>{number}</span>;
};

const ScrollableStepper = ({ steps = [], requote = false, width = 290 }) => {
    const viewportRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);

    const showTooltip = (idx, el) => {
        const step = steps[idx];
        if (!step?.tooltip) return;
        const rect = el.getBoundingClientRect();
        setTooltip({
            text: step.tooltip,
            x: rect.left + rect.width / 2,
            y: rect.top - 8,
        });
    };
    const hideTooltip = () => setTooltip(null);

    useEffect(() => {
        const onScroll = () => setTooltip(null);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, []);

    useEffect(() => {
        if (!steps.length || !viewportRef.current) return undefined;

        let targetIdx = steps.findIndex((s) => s.state === 'active');
        if (targetIdx === -1) targetIdx = steps.findIndex((s) => s.state === 'rej');
        if (targetIdx === -1) {
            for (let i = steps.length - 1; i >= 0; i--) {
                if (isCompletedLike(steps[i].state)) { targetIdx = i; break; }
            }
        }
        if (targetIdx === -1) targetIdx = 0;

        const timer = setTimeout(() => {
            const vp = viewportRef.current;
            if (!vp) return;
            if (vp.scrollWidth > vp.clientWidth) {
                const target = (targetIdx * STEP_WIDTH) - (width / 2) + (STEP_WIDTH / 2);
                vp.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
            }
        }, SCROLL_DELAY);

        return () => clearTimeout(timer);
    }, [steps, width]);

    if (!steps.length) return null;

    return (
        <div css={scrollableStepperCSS(width)} className={requote ? 'is-requote' : ''}>
            <div className="ss-viewport" ref={viewportRef}>
                <div className="ss-track" style={{ width: steps.length * STEP_WIDTH }}>
                    {steps.map((step, i) => {
                        const isFirst = i === 0;
                        const isLast  = i === steps.length - 1;
                        const leftActive  = !isFirst && (isCompletedLike(step.state) || step.state === 'active');
                        const rightActive = !isLast  && isCompletedLike(step.state);

                        return (
                            <div
                                key={`${step.label}-${i}`}
                                className="ss-step"
                                role={step.tooltip ? 'button' : undefined}
                                tabIndex={step.tooltip ? 0 : -1}
                                onMouseEnter={(e) => showTooltip(i, e.currentTarget)}
                                onMouseLeave={hideTooltip}
                                onFocus={(e) => showTooltip(i, e.currentTarget)}
                                onBlur={hideTooltip}
                            >
                                <div className="ss-row">
                                    <span className={`ss-line ${isFirst ? 'transparent' : (leftActive ? 'active' : '')}`} aria-hidden="true" />
                                    <span className={`ss-dot ${step.state || 'pending'}`}>
                                        {renderGlyph(step.state, i + 1)}
                                    </span>
                                    <span className={`ss-line ${isLast ? 'transparent' : (rightActive ? 'active' : '')}`} aria-hidden="true" />
                                </div>
                                <span className={`ss-label ${step.state || 'pending'}`}>{step.label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {tooltip && typeof document !== 'undefined' && createPortal(
                <div
                    css={stepperTooltipCSS()}
                    style={{ left: tooltip.x, top: tooltip.y }}
                    role="tooltip"
                >
                    {tooltip.text}
                </div>,
                document.body,
            )}
        </div>
    );
};

export default ScrollableStepper;
